import { createMedia } from "@artsy/fresnel";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { InView } from "react-intersection-observer";
import {
  Button,
  Container,
  Divider,
  Grid,
  Header,
  Icon,
  Image,
  List,
  GridRow,
  GridColumn,
  ListItem,
  Menu,
  Message,
  Segment,
  Sidebar,
  Item,
  Label,
  SegmentGroup,
  Flag,
  Loader
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { readFromToken, UserRoles } from "../auth.ts";
import { getUnauthorizedUserCount, getWorkingHours } from "../queries.jsx";
import Banner from "./Banner.jsx";
import TextAndIcon from "../TextAndIcon.jsx";
import { useAppContext } from "../AppContext.jsx";

const { MediaContextProvider, Media } = createMedia({
  breakpoints: {
    mobile: 0,
    tablet: 768,
    computer: 1024,
  },
});

const guestPaths = [
    { path: '/', titleKey: 'home' },
]

const userPaths = [
    { path: '/', titleKey: 'home' },
    { path: '/schedule', titleKey: 'schedule-appointment' },
];

const clinicPaths = [
    { path: '/', titleKey: 'home' },
    { path: '/clinic/schedule', titleKey: 'view-schedule' },
    { path: '/clinic/users', titleKey: 'manage-users' },
    { path: '/clinic/config', titleKey: 'edit-config'}
];

const getPaths = () => {
    switch (readFromToken('role')) {
        case UserRoles.AUTHORIZED_COMMANDER:
            return userPaths;
        case UserRoles.AUTHORIZED_DOCTOR:
            return clinicPaths;
        case UserRoles.UNAUTHORIZED_COMMANDER:
        case UserRoles.UNAUTHORIZED_DOCTOR:
        default:
            return guestPaths;
        }
};

const getSignedInMessage = (t) => {
    let msg = `${t('hello')}, `;
    switch (readFromToken('role')) {
        case UserRoles.AUTHORIZED_COMMANDER:
            msg += t('user');
            break;
        case UserRoles.AUTHORIZED_DOCTOR:
            msg += t('admin');
            break;
    }

    return msg;
}

const DesktopContainer = ({ pathname, children, unauthorizedUserCount }) => {
  const [fixed, setFixed] = useState(false);
  const toggleFixedMenu = (inView) => setFixed(!inView);
  const hideBanner = pathname === '/signin' || pathname === '/reset_password';
  const { rtl } = useAppContext();

  const { t } = useTranslation();


  return (
    <Media greaterThan="mobile">
      <InView onChange={toggleFixedMenu}>
        <Segment
          textAlign="center"
          className="segment-banner"
          style={{
            padding: '0',
            color: "#1d2c62",
            fontWeight: 600,
          }}
          vertical
        >
          <Menu
            fixed={fixed ? "top" : null}
            pointing={!fixed}
            secondary={!fixed}
            size="large"
            style={{ backgroundColor: 'rgba(240, 248, 255, 1)' }}
          >
            <Container>
              {
                getPaths().map(({ path, titleKey }) => (
                    <Menu.Item style={{ padding: titleKey === 'manage-users' ? rtl ? '12px 12px 12px 7px' : '12px 7px 12px 12px' : undefined }} key={path} as="a" href={path} active={pathname === path}>
                      <div style={{ margin: titleKey === 'manage-users' ? rtl ? '0 0 0 0.5em' : '0 0.5em 0 0' : undefined }}>{t(titleKey)}</div>
                      <>{titleKey === "manage-users" && unauthorizedUserCount !== 0 && 
                        (<Label color='blue' style={{ margin: 0 }} circular size="tiny">{unauthorizedUserCount}</Label>)}
                      </>
                    </Menu.Item>
                ))
              }
              {
                !!readFromToken() ? (
                    <Menu.Item position={rtl ? "left" : "right"}>
                        <Header as="h4" style={{ margin: '0 15px' }}>{getSignedInMessage(t)}</Header>
                        <Button as="a" basic href="/signout">{t("signout")}</Button>
                    </Menu.Item>
                ) : (
                    pathname !== '/signin' && (
                        <Menu.Item position={!rtl?"right":"left"}>
                            <Button as="a" color="linkedin" href="/signin" style={{ marginLeft: "0.5em" }}> {t('login')}</Button>
                            <Button as="a" href="/signin?form=register" basic style={{ marginLeft: "0.5em" }}>{t('signup')}</Button>
                        </Menu.Item>
                    )
                )
              }
            </Container>
          </Menu>
          <Banner hide={hideBanner} />
        </Segment>
      </InView>
      {children}
    </Media>
  );
};

const MobileContainer = ({ children, pathname, unauthorizedUserCount }) => {
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const handleSidebarHide = () => setSidebarOpened(false);
  const handleToggle = () => setSidebarOpened(true);
  const hideBanner = pathname === '/signin' || pathname === '/reset_password';
  const {rtl} = useAppContext();


  const { t } = useTranslation();

  return (
    <Media as={Sidebar.Pushable} at="mobile">
      <Sidebar.Pushable>
        <Sidebar
          as={Menu}
          animation="overlay"
          className="mobile-sidebar-menu"
          onHide={handleSidebarHide}
          vertical
          visible={sidebarOpened}
          direction={!rtl?"left":"right"}
        >
            {
                getPaths().map(({ path, titleKey }) => (
                    <Menu.Item key={path} as="a" href={path} active={pathname === path}>
                      {t(titleKey)}
                      {titleKey==="manage-users" && unauthorizedUserCount !==0 &&
                        (<Label color='blue' circular size="tiny">{unauthorizedUserCount}</Label>)
                      }
                    </Menu.Item>
                ))
            }
            <br />
            {
                !!readFromToken() ? (
                    <Menu.Item as="a" href="/signout">{t('signout')}</Menu.Item>
                ) : (
                    pathname !== '/signin' && (
                        <>
                            <Menu.Item as="a" href="/signin">{t('login')}</Menu.Item>
                            <Menu.Item as="a" href="/signin?form=register">{t('signup')}</Menu.Item>
                        </>
                    )
                )
            }
        </Sidebar>

        <Sidebar.Pusher dimmed={sidebarOpened}>
          <Segment
            className="segment-banner"
            style={{
                padding: '0',
                color: "#1d2c62",
                fontWeight: 600,
            }}
            vertical
          >
            <Container>
              <Menu pointing secondary size="large">
                <Menu.Item onClick={handleToggle} style={{ paddingTop: 0, paddingRight: '7px' }}>
                  {
                    unauthorizedUserCount !==0 &&
                    (<Label color='blue' circular floating size="mini">{unauthorizedUserCount}</Label>)
                  }
                  <Icon name="sidebar" />
                </Menu.Item>
                {
                    !!readFromToken() ? (
                        <Menu.Item position={rtl ? "left" : "right"}>
                            <Header as="h4" style={{ margin: '0 15px' }}>{getSignedInMessage(t)}</Header>
                            <Button as="a" basic href="/signout">{t("signout")}</Button>
                        </Menu.Item>
                    ) : (
                        pathname !== '/signin' && (
                            <Menu.Item position={!rtl?"right":"left"}>
                                <Button as="a" color="linkedin" href="/signin" style={{ marginLeft: "0.5em" }}>{t("login")}</Button>
                                <Button as="a" href="/signin?form=register" basic style={{ marginLeft: "0.5em" }}>{t("signup")}</Button>
                            </Menu.Item>
                        )
                    )
                }
              </Menu>
            </Container>
            <Banner mobile hide={hideBanner} />
          </Segment>
          {children}
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </Media>
  );
};

const Footer = () => {
    const { t, i18n } = useTranslation();
    const { rtl } = useAppContext();

    const handleLanguageChange = (lang) => {
      localStorage.setItem('lang', lang)
      window.location.reload();
    }

    const {
        data: workingHoursData,
        error,
        isLoading,
        refetch: refetchTable,
    } = useQuery("getWorkingHours", getWorkingHours, {
        onSettled: (response) => {}
    });

    return (
        <Segment attached vertical className="footer-segment">
            <Container>
                <Grid divided stackable>
                    <Grid.Row>
                        <Grid.Column width={6}>
                            <Header as='h4' content={t('opening-hours')} />
                              { isLoading || !workingHoursData ? (
                                <Loader />
                              ) : (
                                <List>
                                  {
                                    ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map((day) => {
                                      const { start, break_start, break_end, end } = workingHoursData[day] || {};
                                      return (
                                        <List.Item>
                                          { rtl
                                            ? `${t(day)}: ${break_end}-${end}, ${start}-${break_start}`
                                            : `${t(day)}: ${start}-${break_start}, ${break_end}-${end}`
                                          }
                                        </List.Item>
                                      )
                                    })
                                  }
                                </List>
                              )}
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Header as='h4' >
                            {t('clinic-phone-title')}
                            </Header>
                            <p>
                            {t('clinic-phone-description')}
                            <a href="https://wa.me/972523006756" target='_blank' rel='noreferrer'>052-3006756</a>
                            </p>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <Button basic color="linkedin" onClick={() => {handleLanguageChange('he') }}>
                            <TextAndIcon flag={true} icon='il' text='עברית'/>
                          </Button>
                          <Button basic color="linkedin" onClick={() => {handleLanguageChange('en') }}>
                            <TextAndIcon flag={true} icon='us' text='English'/>
                          </Button>
                          <br /><br />
                          <b style={{ fontFamily: 'serif', fontSize: '1.5em' }}>{t('signature')}</b>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Container>
        </Segment>
    );
};

export const ResponsiveContainer = ({ children }) => {
    const { pathname } = useLocation();

    const {
      data: unauthorizedUserCount,
      error, // TODO: error
      isLoading,
      refetch: refetchUnauthorizedUserCount,
    } = useQuery("unauthorizedUserCount", getUnauthorizedUserCount, {
      enabled: !!readFromToken() && (readFromToken('role') === UserRoles.AUTHORIZED_DOCTOR)
    }); 

    const { refetchUnauthorizedUserCountRef, rtl } = useAppContext();
    refetchUnauthorizedUserCountRef.current = refetchUnauthorizedUserCount;

    return (
        <div className={rtl ? 'rtl' : ''}>
            <MediaContextProvider>
                <DesktopContainer unauthorizedUserCount={unauthorizedUserCount || 0} pathname={pathname}>{children}</DesktopContainer>
                <MobileContainer unauthorizedUserCount = {unauthorizedUserCount || 0} pathname={pathname}>{children}</MobileContainer>
            </MediaContextProvider>
            <Footer />
        </div>
    );
};
 